import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Loader2 } from 'lucide-react';

export const ProtectedRoute = ({ children, requireAdmin = false, allowedRoles = null }) => {
  const { user, loading, isAuthenticated } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div 
        data-testid="auth-loading-spinner"
        className="min-h-screen bg-slate-900 flex flex-col items-center justify-center text-slate-100"
      >
        <Loader2 className="w-10 h-10 animate-spin text-teal-400 mb-4" />
        <p className="text-sm font-medium tracking-wide text-slate-400">Verifying session security...</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Force-change guard: users flagged must_change_password can only reach /profile/change-password
  if (user?.must_change_password && !location.pathname.startsWith('/profile/change-password')) {
    return <Navigate to="/profile/change-password?forced=1" replace />;
  }

  // Role-aware home fallback — prevents infinite redirect loop when a role is not
  // permitted at the default '/staff/home' (e.g. marketing_executive).
  const ROLE_HOME = {
    marketing_executive: '/marketing/dashboard',
    manager: '/manager/calculations',
    management: '/manager/calculations',
    admin: '/admin/dashboard',
  };
  const roleHome = ROLE_HOME[user?.role] || '/staff/home';

  if (requireAdmin && user?.role !== 'admin') {
    return <Navigate to={roleHome} replace />;
  }

  if (allowedRoles && Array.isArray(allowedRoles) && !allowedRoles.includes(user?.role)) {
    return <Navigate to={roleHome} replace />;
  }

  return children;
};
